<script setup>
import {publicQuote} from '../utils/siteInformation'
import PaymentMethodMark from './PaymentMethodMark.vue'
import { ref, computed, watch, onMounted } from 'vue'
import { api, getToken } from '@/api/client.js'
import { EP } from '@/api/endpoints.js'
import { useCurrencyStore } from '@/stores/currencyStore'
import ButtonSeed from '@/design/primitives/ButtonSeed.vue'
import { useLegalLinks } from '@/router/legalLinks.js'

const props = defineProps({
  open: { type: Boolean, default: false },
  request: {type:Object, default:null}
})
const emit = defineEmits(['close'])
const legalPaths = useLegalLinks()

const currency = useCurrencyStore()

const step = ref(1)              // 1 = amount/currency, 2 = billing
const amount = ref('')
const quickPick = ref(null)
const error = ref('')
const loading = ref(false)
const methodsLoading = ref(false)
const paymentMethods = ref([])
const selectedGatewayId = ref(null)
const paymentConfig = ref(null)
const selectedPackage=ref(null)
const quoteLoading=ref(false)

const checkout = ref({
  firstName: '',
  lastName: '',
  email: '',
  country: '',
  city: '',
  address: '',
  zipCode: '',
  phone: ''
})
const agreed = ref(false)

// Countries list — pulled from admin-managed table via public /api/countries.
// Renders the billing-form Country field as a dropdown so we don't accept free-form text.
const countries = ref([])
onMounted(async () => {
  await Promise.all([currency.load(), currency.loadAvailable()])
  try {
    const res = await api.get(EP.countries)
    if (Array.isArray(res.data)) countries.value = res.data
  } catch {}
  await loadQuote()
})

// Quick-pick presets shown in EUR; displayed in selected currency via rate.
const QUICK_AMOUNTS_EUR = computed(()=>paymentConfig.value?.packages||[])

const symbol = computed(() => currency.symbol)
const rate   = computed(() => Number(currency.rate) || 1)
const selectedCurrency = computed(() => (
  currency.available.find((item) => item.code === currency.code) || null
))
const minimumAmount = computed(()=>paymentConfig.value?.minDeposit)
const amountIssue = computed(()=>{const value=Number(amount.value);if(quoteLoading.value||!paymentConfig.value||paymentConfig.value.available===false)return 'Payment settings are unavailable.';if(!Number.isFinite(value)||value<=0)return 'Enter a valid amount.';if(Math.abs(value*100-Math.round(value*100))>1e-7)return 'Enter an amount with up to two decimal places.';if(value<minimumAmount.value)return `Minimum deposit is ${minimumAmount.value} ${currency.code}.`;if(value>paymentConfig.value.maxDeposit)return `Maximum deposit is ${paymentConfig.value.maxDeposit} ${currency.code}.`;return ''})
let quoteVersion=0
async function loadQuote(){const current=++quoteVersion;quoteLoading.value=true;try{const data=await publicQuote(currency.code);if(current!==quoteVersion)return;if(!selectedPackage.value && (amount.value==='' || paymentConfig.value?.currency!==data.currency)) amount.value=data.minDeposit>0?data.minDeposit:'';paymentConfig.value=data;currency.available=data.currencies||[];currency.select(data.currency);paymentMethods.value=data.methods||[];if(selectedPackage.value){selectedPackage.value=data.packages.find(p=>p.id===selectedPackage.value.id)||null;amount.value=selectedPackage.value?.amount||''}}catch{if(current===quoteVersion)paymentConfig.value=null}finally{if(current===quoteVersion)quoteLoading.value=false}}
function formatQuick(pack){return `${pack.gems} EvoGems · ${pack.amount} ${currency.code}`}
function pickQuick(pack){selectedPackage.value=pack;quickPick.value=pack.id;amount.value=pack.amount}
watch(()=>currency.code,()=>{step.value=1;void loadQuote()})

function reset() {
  step.value = 1
  amount.value = ''
  quickPick.value = null
  error.value = ''
  loading.value = false
  methodsLoading.value = false
  paymentMethods.value = []
  selectedGatewayId.value = null
  agreed.value = false
  checkout.value = {
    firstName: '', lastName: '', email: '',
    country: '', city: '', address: '', zipCode: '', phone: ''
  }
}

function close() {
  reset()
  emit('close')
}

// Reset when modal is re-opened
watch(() => props.open, (now) => {
  if (now) {reset();selectedPackage.value=props.request?.package?.id ? props.request.package:null;amount.value=selectedPackage.value?.amount||'';void loadQuote()}
})

async function loadPaymentMethods() {
  methodsLoading.value = true
  paymentMethods.value = []
  selectedGatewayId.value = null
  try {
    const res = await api.get(EP.paymentsCheckoutMethods, {
      params: { currency: currency.code }
    })
    paymentMethods.value = Array.isArray(res.data?.methods) ? res.data.methods : []
    if (paymentMethods.value.length === 1) {
      selectedGatewayId.value = paymentMethods.value[0].id
    }
  } catch (err) {
    error.value = err.message || 'Failed to load payment methods'
  } finally {
    methodsLoading.value = false
  }
}

async function goToCheckout() {
  error.value = ''
  if(amountIssue.value){error.value=amountIssue.value;return}
  if (!getToken()) {
    close()
    // redirect to sign-in — we don't have a global login modal in evoluxu
    window.location.href = '/register'
    return
  }
  const a = Number(amount.value)
  if (!(minimumAmount.value > 0)) {
    error.value = 'Top-up configuration is temporarily unavailable'
    return
  }
  if (!a || a < minimumAmount.value) {
    error.value = `Enter at least ${symbol.value}${minimumAmount.value.toFixed(2)}`
    return
  }
  await loadPaymentMethods()
  step.value = 2
  if (paymentMethods.value.length === 0 && !error.value) {
    error.value = `No payment method is available for ${currency.code}`
  }
}

function backToAmount() {
  error.value = ''
  step.value = 1
}

const canSubmit = computed(() => {
  const a = Number(amount.value)
  if (amountIssue.value) return false
  const f = checkout.value
  if (!f.firstName.trim() || !f.lastName.trim() || !f.email.trim()) return false
  if (!f.country.trim() || !f.city.trim() || !f.address.trim()) return false
  if (!f.zipCode.trim() || !f.phone.trim()) return false
  if (!agreed.value) return false
  if (!selectedGatewayId.value || methodsLoading.value) return false
  return true
})

const checkoutTotal = computed(() => {
  const a = Number(amount.value)
  if (!a || a <= 0) return `${symbol.value}0`
  return `${symbol.value}${a.toFixed(2)}`
})

// EvoGems credited = EUR value of the charge ({{ paymentConfig?.tokensPerEur }} EvoGems = €1). The entered amount is
// in the selected currency, so convert back to EUR via the rate. Keeping coins tied
// to the EUR value (not the raw amount) means adding new currencies later "just works"
// — only the rate changes, the 1 EvoGem = €1 invariant holds.
const eurValue = computed(() => {
  const a = Number(amount.value)
  if (!a || a <= 0) return 0
  return a / rate.value
})
function fmtNum(n) {
  return Number.isInteger(n) ? String(n) : n.toFixed(2)
}
const coinsDisplay = computed(() => fmtNum(selectedPackage.value?.gems || eurValue.value * Number(paymentConfig.value?.tokensPerEur||0)))
const eurDisplay   = computed(() => fmtNum(Math.round(eurValue.value * 100) / 100))

async function submit() {
  if (loading.value) return
  error.value = ''
  if (!canSubmit.value) {
    error.value = 'Please fill all fields and accept the terms'
    return
  }
  if (!getToken()) {
    error.value = 'Please sign in first'
    return
  }
  loading.value = true
  try {
    const res = await api.post(EP.paymentsInitiate, {
      ...(selectedPackage.value ? {packageId:selectedPackage.value.id} : {customAmount:Number(amount.value)}),
      expectedAmount:Number(amount.value),
      billingInfo: checkout.value,
      currency: currency.code,
      gatewayId: selectedGatewayId.value
    })
    if (res.data?.success && res.data?.redirectUrl) {
      window.location.href = res.data.redirectUrl
    } else {
      error.value = res.data?.message || 'Failed to initiate payment'
      loading.value = false
    }
  } catch (err) {
    error.value = err.response?.data?.message || err.message || 'Payment initiation failed'
    loading.value = false
  }
}
</script>

<template>
  <Teleport to="body">
    <Transition name="topup-fade">
      <div v-if="open" class="topup-overlay" @click.self="close">
        <div class="topup-modal" role="dialog" aria-modal="true" aria-labelledby="topup-title">
          <button class="topup-close" aria-label="Close" @click="close">×</button>

          <!-- STEP 1: amount + currency -->
          <template v-if="step === 1">
            <h2 id="topup-title" class="topup-title u-display u-allcaps">Top Up EvoGems</h2>
            <p class="topup-hint">{{ paymentConfig?.tokensPerEur || '—' }} EvoGems = €1. Pick a package or enter a custom amount.</p>

            <div class="topup-quick">
              <button
                v-for="qa in QUICK_AMOUNTS_EUR"
                :key="qa.id"
                type="button"
                class="topup-quick-btn"
                :class="{ 'topup-quick-btn--active': quickPick === qa.id }"
                @click="pickQuick(qa)" :disabled="!qa.available"
              >{{ formatQuick(qa) }}</button>
            </div>

            <label class="topup-label u-display u-allcaps">Amount ({{ currency.code }})</label>
            <input
              v-model="amount"
              type="number"
              :min="minimumAmount || undefined"
              step="0.01"
              class="topup-input"
              :placeholder="String(minimumAmount || '')"
              @input="quickPick = null; selectedPackage=null"
            />

            <p v-if="amount !== '' && Number.isFinite(Number(amount))" class="topup-hint" aria-live="polite">Total: {{ new Intl.NumberFormat('en', {style: 'currency', currency: currency.code}).format(Number(amount)) }}</p>
            <p v-if="amountIssue" class="topup-error">{{amountIssue}}</p>
            <p v-if="error" class="topup-error">{{ error }}</p>

            <ButtonSeed type="button" size="lg" class="topup-submit" :disabled="!!amountIssue" @click="goToCheckout">
              CONTINUE
            </ButtonSeed>
          </template>

          <!-- STEP 2: billing form -->
          <template v-else>
            <button class="topup-back" type="button" @click="backToAmount">← Back</button>
            <h2 id="topup-title" class="topup-title u-display u-allcaps">Billing details</h2>
            <p class="topup-hint">
              Charged: <strong class="u-gold">{{ checkoutTotal }}</strong>
              <span v-if="currency.code !== 'EUR'" class="topup-eur">≈ €{{ eurDisplay }}</span>
            </p>
            <p class="topup-credit">
              You'll receive <strong class="u-gold">{{ coinsDisplay }} EvoGems</strong>
              <span class="topup-credit-note">({{ paymentConfig?.tokensPerEur }} EvoGems = €1)</span>
            </p>

            <div class="topup-form">
              <div class="topup-row">
                <input v-model="checkout.firstName" class="topup-input" placeholder="First name" />
                <input v-model="checkout.lastName"  class="topup-input" placeholder="Last name" />
              </div>
              <input v-model="checkout.email"   class="topup-input" type="email" placeholder="Email" />
              <select v-model="checkout.country" class="topup-input topup-select">
                <option value="" disabled>Country…</option>
                <option v-for="c in countries" :key="c.iso" :value="c.iso">{{ c.name }}</option>
              </select>
              <input v-model="checkout.city"    class="topup-input" placeholder="City" />
              <input v-model="checkout.address" class="topup-input" placeholder="Address" />
              <div class="topup-row">
                <input v-model="checkout.zipCode" class="topup-input" placeholder="ZIP code" />
                <input v-model="checkout.phone"   class="topup-input" placeholder="Phone" />
              </div>
              <label class="topup-agree">
                <input v-model="agreed" type="checkbox" />
                <span>
                  I have read and agree to the
                  <a :href="legalPaths.terms" target="_blank" rel="noopener noreferrer" class="topup-link" @click.stop>Terms of Use</a>
                  and acknowledge the
                  <a :href="legalPaths.privacy" target="_blank" rel="noopener noreferrer" class="topup-link" @click.stop>Privacy Notice</a>.
                  I understand that my personal data will be processed in accordance with the
                  <a :href="legalPaths.privacy" target="_blank" rel="noopener noreferrer" class="topup-link" @click.stop>Privacy Notice</a>.
                </span>
              </label>
            </div>

            <div class="topup-methods" role="group" aria-label="Payment method">
              <p class="topup-label u-display u-allcaps">Payment method</p>
              <p v-if="methodsLoading" class="topup-hint">Loading available methods…</p>
              <template v-else>
                <button
                  v-for="method in paymentMethods"
                  :key="method.id"
                  type="button"
                  class="topup-method"
                  :class="{ 'topup-method--active': String(selectedGatewayId) === String(method.id) }"
                  :disabled="loading"
                  :aria-pressed="String(selectedGatewayId) === String(method.id)"
                  @click="selectedGatewayId = method.id"
                >
                  <PaymentMethodMark :method="method"/>
                </button>
              </template>
            </div>

            <p v-if="error" class="topup-error">{{ error }}</p>

            <ButtonSeed type="button" size="lg" class="topup-submit" :disabled="loading || !canSubmit" @click="submit">
              {{ loading ? 'REDIRECTING…' : `PAY ${checkoutTotal}` }}
            </ButtonSeed>
          </template>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<style scoped>
.topup-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(0, 0, 0, 0.75);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: var(--sp-4);
}

.topup-modal {
  position: relative;
  width: 100%;
  max-width: 480px;
  max-height: calc(100vh - 32px);
  overflow-y: auto;
  background: var(--bg-deep);
  border: 1px solid #2a2418;
  border-radius: var(--r-md, 8px);
  padding: var(--sp-8) var(--sp-6) var(--sp-6);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
}

.topup-close {
  position: absolute;
  top: var(--sp-3);
  right: var(--sp-3);
  background: transparent;
  border: 0;
  color: var(--ink-soft);
  font-size: 26px;
  line-height: 1;
  cursor: pointer;
  padding: 4px 10px;
}
.topup-close:hover { opacity:.72; filter:none; }

.topup-back {
  background: transparent;
  border: 0;
  color: var(--ink-mute);
  font-size: var(--fs-12);
  letter-spacing: 0.1em;
  cursor: pointer;
  padding: 0;
  margin-bottom: var(--sp-2);
}
.topup-back:hover { color: var(--gold); }

.topup-title {
  font-size: clamp(var(--fs-18), 2vw, var(--fs-24));
  color: var(--gold);
  letter-spacing: 0.12em;
  margin: 0 0 var(--sp-2);
}

.topup-hint {
  font-size: var(--fs-12);
  color: var(--ink-mute);
  margin: 0 0 var(--sp-5);
  line-height: 1.5;
}
.topup-eur {
  margin-left: var(--sp-2);
  color: var(--ink-mute);
}

/* Coins-credited line in the billing step */
.topup-credit {
  font-size: var(--fs-13);
  color: var(--ink-soft);
  margin: calc(-1 * var(--sp-3)) 0 var(--sp-5);
  line-height: 1.5;
}
.topup-credit-note {
  margin-left: var(--sp-2);
  font-size: var(--fs-11);
  color: var(--ink-mute);
}

/* Clickable legal links inside the agree checkbox */
.topup-link {
  position: relative;
  z-index: 2;
  color: var(--gold);
  text-decoration: underline;
  text-underline-offset: 2px;
  pointer-events: auto;
}
.topup-link:hover { color: var(--gold-deep, var(--gold)); }

.topup-quick {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--sp-2);
  margin-bottom: var(--sp-7);
}

.topup-quick-btn {
  background: transparent;
  border: 1px solid var(--ash);
  color: var(--ink-soft);
  padding: var(--sp-2) var(--sp-3);
  border-radius: var(--r-xs, 4px);
  font-family: var(--font-display);
  font-size: var(--fs-13);
  letter-spacing: 0.06em;
  cursor: pointer;
  transition: all var(--t-fast) var(--ease-cinema);
}
.topup-quick-btn:hover {
  border-color: rgba(229, 199, 107, 0.4);
  color: var(--gold);
}
.topup-quick-btn--active {
  background: rgba(229, 199, 107, 0.12);
  border-color: var(--gold);
  color: var(--gold);
}

.topup-label {
  display: block;
  font-size: var(--fs-11);
  color: var(--ink-mute);
  letter-spacing: 0.12em;
  margin: var(--sp-3) 0 var(--sp-2);
}

.topup-select {
  /* select element inherits .topup-input box but needs custom arrow + cursor */
  appearance: none;
  -webkit-appearance: none;
  background-image: linear-gradient(45deg, transparent 50%, var(--gold) 50%),
                    linear-gradient(135deg, var(--gold) 50%, transparent 50%);
  background-position: calc(100% - 18px) center, calc(100% - 13px) center;
  background-size: 5px 5px, 5px 5px;
  background-repeat: no-repeat;
  padding-right: 32px;
  cursor: pointer;
}
.topup-select option { background: var(--bg-deep); color: var(--ink); }

.topup-input {
  width: 100%;
  background: var(--bg-deep);
  border: 1px solid var(--ash);
  border-radius: var(--r-xs, 4px);
  color: var(--ink);
  padding: 12px 14px;
  font-size: var(--fs-14);
  font-family: inherit;
  transition: border-color var(--t-fast) var(--ease-cinema);
}
.topup-input:focus {
  outline: none;
  border-color: var(--gold);
}

.topup-form {
  display: flex;
  flex-direction: column;
  gap: var(--sp-3);
  margin-bottom: var(--sp-4);
}

.topup-methods {
  display: grid;
  gap: var(--sp-2);
  margin: var(--sp-4) 0;
}

.topup-methods .topup-label { margin-top: 0; }

.topup-method {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--sp-2);
  width: 100%;
  padding: 11px 14px;
  border: 1px solid var(--ash);
  border-radius: var(--r-xs, 4px);
  background: transparent;
  color: var(--ink-soft);
  font: inherit;
  cursor: pointer;
}

.topup-method--active {
  border-color: var(--gold);
  background: rgba(229, 199, 107, 0.12);
  color: var(--gold);
}

.topup-method:disabled { opacity: 0.55; cursor: not-allowed; }
.topup-method-logos { display: inline-flex; align-items: center; gap: 5px; }
.topup-method-logos img { width: auto; height: 20px; object-fit: contain; }

.topup-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--sp-3);
}

.topup-agree {
  display: flex;
  align-items: flex-start;
  gap: var(--sp-2);
  font-size: var(--fs-12);
  color: var(--ink-soft);
  line-height: 1.4;
  cursor: pointer;
  margin-top: var(--sp-1);
}
.topup-agree input {
  margin-top: 3px;
  accent-color: var(--gold);
}

.topup-error {
  font-size: var(--fs-12);
  color: var(--ember);
  margin: var(--sp-2) 0 var(--sp-3);
  letter-spacing: 0.04em;
}

.topup-submit {
  width: 100%;
  margin-top: var(--sp-3);
}

@media (max-width: 500px) {
  .topup-modal { padding: var(--sp-6) var(--sp-4) var(--sp-4); }
  .topup-quick { grid-template-columns: repeat(2, 1fr); }
  .topup-row   { grid-template-columns: 1fr; }
}

/* Transition */
.topup-fade-enter-active,
.topup-fade-leave-active {
  transition: opacity 0.18s var(--ease-cinema);
}
.topup-fade-enter-active .topup-modal,
.topup-fade-leave-active .topup-modal {
  transition: transform 0.18s var(--ease-cinema);
}
.topup-fade-enter-from { opacity: 0; }
.topup-fade-leave-to   { opacity: 0; }
.topup-fade-enter-from .topup-modal,
.topup-fade-leave-to .topup-modal {
  transform: translateY(20px);
}

.topup-close:hover{ opacity:.72; filter:none; }
.topup-input::placeholder{color:inherit;opacity:.65}
</style>
